import { Segment } from '@ftr/api-core'
import { LocalTimeRange } from '@ftr/contracts/type/shared'
import { isNotNullOrUndefined, iterateAllSorted, mapIt, minBy } from '@ftr/foundation'
import { Duration, Instant, LocalDateTime, LocalTime, convert } from '@js-joda/core'
import { isEqual, uniq } from 'lodash-es'
import DataIntervalTree from 'node-interval-tree'
import { match } from 'ts-pattern'
import { HearingSectionModel } from '../store'
import {
  RealTimeSttRemarkUtils,
  getContainingHearingSectionId,
  getContainingSealingMarkerId,
  getContainingSessionMarkerId,
  isTimeRangeWithinTimeRanges,
  isTimeWithinTimeRanges,
} from '../utils'
import { RealTimeSttHearingMarker } from './real-time-stt-hearing-marker'
import { RealTimeSttPause, RealTimeSttPauseConfiguration, pausesEqual, shouldIgnorePause } from './real-time-stt-pause'
import { RealTimeSttRemark } from './real-time-stt-remark'
import { RealTimeSearchHighlights, RealTimeSttRemarkWithMetadata } from './real-time-stt-remark-with-metadata'
import { RealTimeSttSealedSegment } from './real-time-stt-sealed-segment'
import { RealTimeSttSealingMarker } from './real-time-stt-sealing-marker'
import { RealTimeSttSessionMarker } from './real-time-stt-session-marker'
import {
  RealTimeSttMetadata,
  RealTimeSttTranscriptElement,
  RealTimeSttTranscriptElementWithMetadata,
  RemarkMetadata,
  isHearingMarker,
  isPause,
  isSealingMarker,
  isSessionMarker,
} from './real-time-stt-transcript-element'
import { compareTranscriptElements } from './real-time-stt-transcript-elements-comparator'

type ReadonlyDataIntervalTree<T> = Omit<DataIntervalTree<T>, 'insert' | 'remove'>
type ReadonlyMap<K, V> = Omit<Map<K, V>, 'set' | 'clear' | 'delete'>

export interface RealTimeSttSegmentPauseMetadata {
  readonly configuration: RealTimeSttPauseConfiguration
  readonly firstPauseBreakingElement: PauseBreakingTranscriptElement | undefined
  readonly lastPauseBreakingElement: PauseBreakingTranscriptElement | undefined
}

type RealTimeSttSegmentPauseMetadataCalculationContainer = RealTimeSttSegmentPauseMetadata & {
  readonly pausesBetweenRemarksMutable: DataIntervalTree<RealTimeSttPause>
}

// Pauses are calculated between the following transcript elements
export type PauseBreakingTranscriptElement =
  | RealTimeSttRemark
  | RealTimeSttSessionMarker
  | RealTimeSttSealingMarker
  | RealTimeSttHearingMarker

/**
 * A RealTimeSttSegment is an immutable object and is the data that is stored in the RealTimeSttSegmentMap. Remarks are
 * stored in a few structures to optimise the different ways that we query them. We use an interval tree for querying
 * based on time, and a simple array for in order traversal.
 */
export class RealTimeSttSegment extends Segment {
  /**
   * @see createEmpty
   * @see rebuildWithRemarks
   */
  private constructor(
    override readonly high: number,
    override readonly low: number,
    override readonly id: string,
    private readonly remarksTree: ReadonlyDataIntervalTree<RealTimeSttRemark>,
    readonly remarksArray: readonly RealTimeSttRemark[],
    private readonly liveResultToRemarkMap: ReadonlyMap<string, readonly RealTimeSttRemark[]>,
    readonly lastRemark: RealTimeSttRemark | undefined,
    readonly overflows: boolean,
    readonly hasRemarkContent: boolean,
    readonly pauseMetadata: RealTimeSttSegmentPauseMetadata,
    readonly leadingPause: RealTimeSttPause | undefined,
    private readonly pausesBetweenRemarks: ReadonlyDataIntervalTree<RealTimeSttPause>,
    readonly sealedSegments: readonly RealTimeSttSealedSegment[],
    readonly searchHighlights: RealTimeSearchHighlights,
    readonly sessionMarkers: readonly RealTimeSttSessionMarker[],
    readonly sealingMarkers: readonly RealTimeSttSealingMarker[],
    readonly hearings: readonly HearingSectionModel[],
    readonly hearingMarkers: readonly RealTimeSttHearingMarker[],
    readonly allSealingMarkers: readonly RealTimeSttSealingMarker[],
    readonly allSessionMarkers: readonly RealTimeSttSessionMarker[],
    readonly hideAfter: LocalDateTime | undefined,
  ) {
    super(high, low, id)
  }

  static createEmpty(
    id: string,
    low: number,
    high: number,
    pauseConfiguration: RealTimeSttPauseConfiguration,
    searchHighlights: RealTimeSearchHighlights,
    hideAfter: LocalDateTime | undefined,
  ): RealTimeSttSegment {
    return new RealTimeSttSegment(
      high,
      low,
      id,
      new DataIntervalTree<RealTimeSttRemark>(),
      [],
      new Map(),
      undefined,
      false,
      false,
      {
        configuration: pauseConfiguration,
        firstPauseBreakingElement: undefined,
        lastPauseBreakingElement: undefined,
      },
      undefined,
      new DataIntervalTree<RealTimeSttPause>(),
      [],
      searchHighlights,
      [],
      [],
      [],
      [],
      [],
      [],
      hideAfter,
    )
  }

  rebuildWithRemarks(remarks: RealTimeSttRemark[]): RealTimeSttSegment {
    const allRemarksSorted = remarks.sort(compareTranscriptElements)

    const sortedPauseBreakingTranscriptElements = [
      ...allRemarksSorted,
      ...this.hearingMarkers,
      ...this.sessionMarkers,
      ...this.sealingMarkers,
    ].sort(compareTranscriptElements)

    const liveResultToRemarkMap = new Map<string, RealTimeSttRemark[]>()
    const intervalTree = new DataIntervalTree<RealTimeSttRemark>()
    let lastRemark: RealTimeSttRemark | undefined = undefined
    let hasRemarkContent = false

    const sealedTimeRanges = uniq(this.allSealingMarkers.map(sm => sm.sealedTimeRange))

    let pauseMetadata: RealTimeSttSegmentPauseMetadataCalculationContainer = {
      configuration: this.pauseMetadata.configuration,
      firstPauseBreakingElement: undefined,
      lastPauseBreakingElement: undefined,
      pausesBetweenRemarksMutable: new DataIntervalTree<RealTimeSttPause>(),
    }
    for (const pauseBreakingTranscriptElement of sortedPauseBreakingTranscriptElements) {
      if (pauseBreakingTranscriptElement.type === 'Remark') {
        intervalTree.insert(
          convert(pauseBreakingTranscriptElement.startTime).toEpochMilli(),
          convert(pauseBreakingTranscriptElement.endTime).toEpochMilli(),
          pauseBreakingTranscriptElement,
        )

        if (pauseBreakingTranscriptElement.liveStatus) {
          const liveRemarksWithId = liveResultToRemarkMap.get(pauseBreakingTranscriptElement.id) ?? []
          liveRemarksWithId.push(pauseBreakingTranscriptElement)
          liveResultToRemarkMap.set(pauseBreakingTranscriptElement.id, liveRemarksWithId)
        }
        if (!lastRemark || pauseBreakingTranscriptElement.endTime.isAfter(lastRemark.endTime)) {
          lastRemark = pauseBreakingTranscriptElement
        }
        hasRemarkContent = hasRemarkContent || pauseBreakingTranscriptElement.content.length > 0
      }

      pauseMetadata = RealTimeSttSegment.calculatePauseMetadataForRemark(
        pauseMetadata,
        pauseBreakingTranscriptElement,
        sealedTimeRanges,
      )
    }
    const overflows = lastRemark ? convert(lastRemark.endTime).toEpochMilli() > this.high : false

    return new RealTimeSttSegment(
      this.high,
      this.low,
      this.id,
      intervalTree,
      allRemarksSorted,
      liveResultToRemarkMap,
      lastRemark,
      overflows,
      hasRemarkContent,
      {
        configuration: pauseMetadata.configuration,
        firstPauseBreakingElement: pauseMetadata.firstPauseBreakingElement,
        lastPauseBreakingElement: pauseMetadata.lastPauseBreakingElement,
      },
      this.leadingPause,
      pauseMetadata.pausesBetweenRemarksMutable,
      this.sealedSegments,
      this.searchHighlights,
      this.sessionMarkers,
      this.sealingMarkers,
      this.hearings,
      this.hearingMarkers,
      this.allSealingMarkers,
      this.allSessionMarkers,
      this.hideAfter,
    )
  }

  withRemovedRemark(id: string): RealTimeSttSegment {
    return this.rebuildWithRemarks([...this.remarksTree.inOrder()].map(x => x.data).filter(remark => remark.id !== id))
  }

  findRemarkById(id: string): RealTimeSttRemark | undefined {
    return this.remarksArray.find(remark => remark.id === id)
  }

  static calculatePauseMetadataForRemark(
    {
      configuration,
      firstPauseBreakingElement,
      lastPauseBreakingElement,
      pausesBetweenRemarksMutable,
    }: RealTimeSttSegmentPauseMetadataCalculationContainer,
    pauseBreakingTranscriptElement: PauseBreakingTranscriptElement,
    sealedTimeRanges: LocalTimeRange[],
  ): RealTimeSttSegmentPauseMetadataCalculationContainer {
    if (
      !firstPauseBreakingElement ||
      compareTranscriptElements(firstPauseBreakingElement, pauseBreakingTranscriptElement) > 0
    ) {
      firstPauseBreakingElement = pauseBreakingTranscriptElement
    }
    if (
      lastPauseBreakingElement !== undefined &&
      pauseBreakingTranscriptElement.startTime.isAfter(lastPauseBreakingElement.endTime)
    ) {
      const pauseBetweenRemarks = RealTimeSttPause.betweenPauseBreakingElements(
        lastPauseBreakingElement,
        pauseBreakingTranscriptElement,
      )

      const ignorePause = shouldIgnorePause(
        configuration,
        pauseBetweenRemarks,
        lastPauseBreakingElement,
        pauseBreakingTranscriptElement,
        sealedTimeRanges,
      )
      if (!ignorePause) {
        pausesBetweenRemarksMutable.insert(
          convert(pauseBetweenRemarks.startTime).toEpochMilli(),
          convert(pauseBetweenRemarks.endTime).toEpochMilli(),
          pauseBetweenRemarks,
        )
      }
    }
    if (
      lastPauseBreakingElement === undefined ||
      compareTranscriptElements(pauseBreakingTranscriptElement, lastPauseBreakingElement, 'endTime') > 0
    ) {
      lastPauseBreakingElement = pauseBreakingTranscriptElement
    }

    return {
      configuration,
      firstPauseBreakingElement,
      lastPauseBreakingElement,
      pausesBetweenRemarksMutable,
    }
  }

  get numRemarks(): number {
    return this.remarksTree.count
  }

  rebuildWithReplacedOrAddedRemarks(newRemarks: RealTimeSttRemark[]): RealTimeSttSegment {
    const updatedRemarks = Array.from(this.remarksTree.inOrder())
      .map(x => x.data)
      // Remove any previous remarks that will be replaced by newer ones
      .filter(remark => !newRemarks.find(newRemark => remark.id === newRemark.id))
      .concat(newRemarks)
    return this.rebuildWithRemarks(updatedRemarks)
  }

  withLeadingPause(leadingPause: RealTimeSttPause | undefined): RealTimeSttSegment {
    if (leadingPause === this.leadingPause || pausesEqual(leadingPause, this.leadingPause)) {
      return this
    }
    return new RealTimeSttSegment(
      this.high,
      this.low,
      this.id,
      this.remarksTree,
      this.remarksArray,
      this.liveResultToRemarkMap,
      this.lastRemark,
      this.overflows,
      this.hasRemarkContent,
      this.pauseMetadata,
      leadingPause,
      this.pausesBetweenRemarks,
      this.sealedSegments,
      this.searchHighlights,
      this.sessionMarkers,
      this.sealingMarkers,
      this.hearings,
      this.hearingMarkers,
      this.allSealingMarkers,
      this.allSessionMarkers,
      this.hideAfter,
    )
  }

  withHideAfter(hideAfter: LocalDateTime): RealTimeSttSegment {
    // To avoid frequent updates map this to a semantically equivalent value relevant to this segment.
    // i.e. the start of segment if hideAfter is before segment, undefined if after segment, otherwise provided value
    const hideAfterForSegment = match(convert(hideAfter).toEpochMilli())
      .when(
        hideAfterMillis => hideAfterMillis < this.low,
        () => LocalDateTime.ofInstant(Instant.ofEpochMilli(this.low)),
      )
      .when(
        hideAfterMillis => hideAfterMillis > this.high,
        () => undefined,
      )
      .otherwise(() => hideAfter)
    if (hideAfterForSegment === this.hideAfter || hideAfterForSegment?.equals(this.hideAfter)) {
      return this
    }

    return new RealTimeSttSegment(
      this.high,
      this.low,
      this.id,
      this.remarksTree,
      this.remarksArray,
      this.liveResultToRemarkMap,
      this.lastRemark,
      this.overflows,
      this.hasRemarkContent,
      this.pauseMetadata,
      this.leadingPause,
      this.pausesBetweenRemarks,
      this.sealedSegments,
      this.searchHighlights,
      this.sessionMarkers,
      this.sealingMarkers,
      this.hearings,
      this.hearingMarkers,
      this.allSealingMarkers,
      this.allSessionMarkers,
      hideAfterForSegment,
    )
  }

  getRemarksDuring(start: LocalDateTime, end: LocalDateTime): RealTimeSttRemarkWithMetadata[] {
    const startEpoch = convert(start).toEpochMilli()
    const endEpoch = convert(end).toEpochMilli()
    const remarkIds = new Set(this.remarksTree.search(startEpoch, endEpoch).map(remark => remark.id))
    return this.transcriptElements.filter((x): x is RealTimeSttRemarkWithMetadata => remarkIds.has(x.id))
  }

  getRemarksAt(time: LocalDateTime): RealTimeSttRemarkWithMetadata[] {
    const epochTime = convert(time).toEpochMilli()
    const remarkIds = new Set(this.remarksTree.search(epochTime, epochTime).map(remark => remark.id))
    return this.transcriptElements.filter((x): x is RealTimeSttRemarkWithMetadata => remarkIds.has(x.id))
  }

  getPauseAt(time: LocalDateTime): RealTimeSttPause | undefined {
    const epochTime = convert(time).toEpochMilli()
    const pausesBetweenRemarks = this.pausesBetweenRemarks.search(epochTime, epochTime)

    // There can only ever be one pause at a given time
    if (pausesBetweenRemarks.length) {
      return pausesBetweenRemarks[0]
    }

    if (this.leadingPause && pauseIntersectsWithTime(this.leadingPause, time)) {
      return this.leadingPause
    }

    return undefined
  }

  rebuildWithUpdatedSearchHighlights(searchHighlights: RealTimeSearchHighlights): RealTimeSttSegment {
    const withUpdatedSearchHighlights = new RealTimeSttSegment(
      this.high,
      this.low,
      this.id,
      this.remarksTree,
      this.remarksArray,
      this.liveResultToRemarkMap,
      this.lastRemark,
      this.overflows,
      this.hasRemarkContent,
      this.pauseMetadata,
      this.leadingPause,
      this.pausesBetweenRemarks,
      this.sealedSegments,
      searchHighlights,
      this.sessionMarkers,
      this.sealingMarkers,
      this.hearings,
      this.hearingMarkers,
      this.allSealingMarkers,
      this.allSessionMarkers,
      this.hideAfter,
    )

    return withUpdatedSearchHighlights.rebuildWithRemarks([...this.remarksTree.inOrder()].map(x => x.data))
  }

  /**
   * Rebuilds the segment with the given session markers for the entire transcript
   */
  rebuildWithSessionMarkers(allSessionMarkers: RealTimeSttSessionMarker[]): RealTimeSttSegment {
    const markersRelevantToSegment: RealTimeSttSessionMarker[] = allSessionMarkers
      .filter(marker => this.markerIsWithinSegmentBounds(marker))
      .sort(compareTranscriptElements)

    const withUpdatedSessionMarkers = new RealTimeSttSegment(
      this.high,
      this.low,
      this.id,
      this.remarksTree,
      this.remarksArray,
      this.liveResultToRemarkMap,
      this.lastRemark,
      this.overflows,
      this.hasRemarkContent,
      this.pauseMetadata,
      this.leadingPause,
      this.pausesBetweenRemarks,
      this.sealedSegments,
      this.searchHighlights,
      markersRelevantToSegment,
      this.sealingMarkers,
      this.hearings,
      this.hearingMarkers,
      this.allSealingMarkers,
      allSessionMarkers,
      this.hideAfter,
    )

    return withUpdatedSessionMarkers.rebuildWithRemarks([...this.remarksTree.inOrder()].map(x => x.data))
  }

  /**
   * Rebuilds the segment with the given hearing markers for the entire transcript.
   * We intentionally save hearing markers relevant to the segment and all hearings regardless of the segment.
   * Relevant hearing markers are used to display and sort transcript elements within the current segment.
   * All hearings are used to mark remarks with isHearing for styling, even if a hearing spans across multiple segments.
   */
  rebuildWithHearingMarkers(
    hearingMarkers: RealTimeSttHearingMarker[],
    hearings: HearingSectionModel[],
  ): RealTimeSttSegment {
    if (isEqual(hearings, this.hearings)) {
      return this
    }

    const hearingMarkersRelevantToSegment: RealTimeSttHearingMarker[] = hearingMarkers
      .filter(marker => this.markerIsWithinSegmentBounds(marker))
      .filter(marker => !(marker.hearingAnnotation.showAsOngoing && marker.dividerType === 'hearingEnd'))
      .sort(compareTranscriptElements)

    const withUpdatedHearingElements = new RealTimeSttSegment(
      this.high,
      this.low,
      this.id,
      this.remarksTree,
      this.remarksArray,
      this.liveResultToRemarkMap,
      this.lastRemark,
      this.overflows,
      this.hasRemarkContent,
      this.pauseMetadata,
      this.leadingPause,
      this.pausesBetweenRemarks,
      this.sealedSegments,
      this.searchHighlights,
      this.sessionMarkers,
      this.sealingMarkers,
      hearings,
      hearingMarkersRelevantToSegment,
      this.allSealingMarkers,
      this.allSessionMarkers,
      this.hideAfter,
    )

    return withUpdatedHearingElements.rebuildWithRemarks(
      [...this.remarksTree.inOrder()].map(x => ({
        ...x.data,
        isHearing: shouldMarkAsHearing(x.data, hearings),
        hearingSectionId: getHearingSectionId(x.data, hearings),
      })),
    )
  }

  rebuildWithSealingMarkers(allSealingMarkers: RealTimeSttSealingMarker[]): RealTimeSttSegment {
    if (isEqual(allSealingMarkers, this.allSealingMarkers)) {
      return this
    }

    const markersRelevantToSegment: RealTimeSttSealingMarker[] = allSealingMarkers
      .filter(marker => this.markerIsWithinSegmentBounds(marker))
      .sort(compareTranscriptElements)

    const withUpdatedSealingMarkers = new RealTimeSttSegment(
      this.high,
      this.low,
      this.id,
      this.remarksTree,
      this.remarksArray,
      this.liveResultToRemarkMap,
      this.lastRemark,
      this.overflows,
      this.hasRemarkContent,
      this.pauseMetadata,
      this.leadingPause,
      this.pausesBetweenRemarks,
      this.sealedSegments,
      this.searchHighlights,
      this.sessionMarkers,
      markersRelevantToSegment,
      this.hearings,
      this.hearingMarkers,
      allSealingMarkers,
      this.allSessionMarkers,
      this.hideAfter,
    )

    return withUpdatedSealingMarkers.rebuildWithRemarks(
      [...this.remarksTree.inOrder()].map(x => ({
        ...x.data,
        isSealed: shouldMarkAsSealed(x.data, allSealingMarkers),
        sealingMarkerId: getSealingMarkerId(x.data, allSealingMarkers),
      })),
    )
  }

  rebuildWithPauseConfiguration(pauseConfiguration: RealTimeSttPauseConfiguration): RealTimeSttSegment {
    if (isEqual(pauseConfiguration, this.pauseMetadata.configuration)) {
      return this
    }

    const pauseMetadata = {
      configuration: pauseConfiguration,
      firstPauseBreakingElement: this.pauseMetadata.firstPauseBreakingElement,
      lastPauseBreakingElement: this.pauseMetadata.lastPauseBreakingElement,
    }

    return new RealTimeSttSegment(
      this.high,
      this.low,
      this.id,
      this.remarksTree,
      this.remarksArray,
      this.liveResultToRemarkMap,
      this.lastRemark,
      this.overflows,
      this.hasRemarkContent,
      pauseMetadata,
      this.leadingPause,
      this.pausesBetweenRemarks,
      this.sealedSegments,
      this.searchHighlights,
      this.sessionMarkers,
      this.sealingMarkers,
      this.hearings,
      this.hearingMarkers,
      this.allSealingMarkers,
      this.allSessionMarkers,
      this.hideAfter,
    )
  }

  readonly transcriptElements = Array.from(this.computeTranscriptElements())

  private *computeTranscriptElements(): IterableIterator<RealTimeSttTranscriptElementWithMetadata> {
    if (this.leadingPause) {
      yield this.addMetadata(this.leadingPause, undefined)
    }
    const sortedTranscriptElements = iterateAllSorted<RealTimeSttTranscriptElement>(
      [
        mapIt(this.remarksTree.inOrder(), x => x.data),
        mapIt(this.pausesBetweenRemarks.inOrder(), x => x.data),
        this.hearingMarkers[Symbol.iterator](),
        this.sessionMarkers[Symbol.iterator](),
        this.sealingMarkers[Symbol.iterator](),
      ],
      compareTranscriptElements,
    )
    let previousItem: RealTimeSttTranscriptElementWithMetadata | undefined = undefined
    for (const item of sortedTranscriptElements) {
      const itemWithMetadata = this.addMetadata(item, previousItem)
      yield itemWithMetadata
      previousItem = itemWithMetadata
    }
  }

  getClosestTranscriptElement(
    time: LocalDateTime,
    sequence?: 'before' | 'after',
  ): RealTimeSttTranscriptElementWithMetadata | undefined {
    const diffBetweenElementAndTime = (element: RealTimeSttTranscriptElementWithMetadata): number => {
      if (sequence === 'before' && element.startTime.isAfter(time)) return LocalTime.MAX.toNanoOfDay()
      if (sequence === 'after' && element.startTime.isBefore(time)) return LocalTime.MAX.toNanoOfDay()
      return Duration.between(element.startTime, time).abs().toMillis()
    }

    if (time.isBefore(this.getLocalStart()) || time.isEqual(this.getLocalStart())) {
      if (sequence === 'before') {
        return undefined
      }
      const firstRemark: RealTimeSttRemark | undefined = this.remarksTree.inOrder().next().value?.data
      return minBy(
        [firstRemark, this.leadingPause].filter(isNotNullOrUndefined).map(x => this.addMetadata(x, undefined)),
        diffBetweenElementAndTime,
      )
    }

    if (time.isAfter(this.getLocalEnd())) {
      if (sequence === 'after') {
        return undefined
      }
      return this.remarksTree.reverseInOrder().next().value?.data
    }

    const closestElement = minBy(this.transcriptElements, diffBetweenElementAndTime)
    if (closestElement && sequence === 'before' && closestElement.startTime.isAfter(time)) return undefined
    if (closestElement && sequence === 'after' && closestElement.startTime.isBefore(time)) return undefined
    return closestElement
  }

  private markerIsWithinSegmentBounds(marker: RealTimeSttTranscriptElement): boolean {
    return (
      (marker.startTime.isEqual(this.getLocalStart()) || marker.startTime.isAfter(this.getLocalStart())) &&
      marker.startTime.isBefore(this.getLocalEnd())
    )
  }

  private addMetadata(
    item: RealTimeSttTranscriptElement,
    previousItem: RealTimeSttTranscriptElementWithMetadata | undefined,
  ): RealTimeSttTranscriptElementWithMetadata {
    const sessionMarkerId = getSessionMarkerId(item, this.allSessionMarkers)
    const isInSession = sessionMarkerId !== undefined

    return addMetadata(
      item,
      shouldMarkAsSealed(item, this.allSealingMarkers),
      this.id,
      isInSession,
      shouldMarkAsHearing(item, this.hearings),
      shouldMarkAsAutoGenerated(item, this.hearings),
      shouldMarkAsHidden(item, this.hideAfter),
      getHearingSectionId(item, this.hearings),
      getSealingMarkerId(item, this.allSealingMarkers),
      sessionMarkerId,
      getRemarkMetadata(item, this.searchHighlights, previousItem),
    )
  }
}

/**
 * Checks start/end inclusive to match behaviour of the 'search' in the interval tree. Useful when checking if the
 * leading pause intersects with a time.
 * @param pause RealTimeSttPause to check if time intersects with
 * @param time The time which to check if the pause intersects with
 */
function pauseIntersectsWithTime(pause: RealTimeSttPause, time: LocalDateTime): boolean {
  const timeIsInsideLeadingPause = pause.startTime.isBefore(time) && pause.endTime.isAfter(time)
  const timeEqualsStartOrEndOfLeadingPause = pause.startTime.isEqual(time) || pause.endTime.isEqual(time)

  return timeIsInsideLeadingPause || timeEqualsStartOrEndOfLeadingPause
}

function shouldMarkAsSealed(
  item: RealTimeSttTranscriptElement,
  sealingMarkers: readonly RealTimeSttSealingMarker[],
): boolean {
  // Avoid marking sealing and hearing markers
  if (isSealingMarker(item) || isHearingMarker(item)) {
    return false
  }

  const sealedTimeRanges = sealingMarkers.map(s => s.sealedTimeRange)

  return shouldMarkItemWithinTimeRanges(item, sealedTimeRanges)
}

function shouldMarkAsHearing(item: RealTimeSttTranscriptElement, hearings: readonly HearingSectionModel[]): boolean {
  // Avoid marking hearing markers
  if (isHearingMarker(item)) {
    return false
  }

  const hearingTimeRanges = hearings.map(h => new LocalTimeRange(h.startTime, h.endTime))

  if (isSealingMarker(item)) {
    // Mark sealing markers if sealed segment is within a hearing (e.g. for sealing markers
    // we check if sealed time range is within a hearing time range, not just a start time)
    return isTimeRangeWithinTimeRanges(item.sealedTimeRange, hearingTimeRanges)
  }

  return shouldMarkItemWithinTimeRanges(item, hearingTimeRanges)
}

function getHearingSectionId(
  item: RealTimeSttTranscriptElement,
  hearings: readonly HearingSectionModel[],
): string | undefined {
  if (isSessionMarker(item)) {
    return undefined
  }

  return getContainingHearingSectionId(item.startTime.toLocalTime(), hearings)
}

function shouldMarkAsAutoGenerated(
  item: RealTimeSttTranscriptElement,
  hearings: readonly HearingSectionModel[],
): boolean {
  if (isSessionMarker(item)) {
    return false
  }
  if (item.type === 'HearingMarker') {
    return item.hearingAnnotation.isAutoGenerated
  }

  const hearingTimeRanges = hearings.filter(x => x.isAutoGenerated).map(h => new LocalTimeRange(h.startTime, h.endTime))

  if (isSealingMarker(item)) {
    // Mark sealing markers if sealed segment is within a hearing (e.g. for sealing markers
    // we check if sealed time range is within a hearing time range, not just a start time)
    return isTimeRangeWithinTimeRanges(item.sealedTimeRange, hearingTimeRanges)
  }

  return shouldMarkItemWithinTimeRanges(item, hearingTimeRanges)
}

function shouldMarkAsHidden(item: RealTimeSttTranscriptElement, hideAfter: LocalDateTime | undefined): boolean {
  if (hideAfter === undefined) {
    return false
  }
  return item.startTime.isAfter(hideAfter) || item.startTime.equals(hideAfter)
}

function getSealingMarkerId(
  item: RealTimeSttTranscriptElement,
  sealingMarkers: readonly RealTimeSttSealingMarker[],
): string | undefined {
  if (isHearingMarker(item) || isSessionMarker(item)) {
    return undefined
  }

  return getContainingSealingMarkerId(item.startTime.toLocalTime(), sealingMarkers)
}

function getSessionMarkerId(
  item: RealTimeSttTranscriptElement,
  sessionMarkers: readonly RealTimeSttSessionMarker[],
): string | undefined {
  return getContainingSessionMarkerId(item.startTime.toLocalTime(), sessionMarkers)
}

function shouldMarkItemWithinTimeRanges(
  item: RealTimeSttTranscriptElement,
  timeRanges: readonly LocalTimeRange[],
): boolean {
  // Avoid marking session markers
  if (isSessionMarker(item)) {
    return false
  }

  // Avoid marking breaks between sessions
  if (isPause(item) && item.pauseType === 'session-break') {
    return false
  }

  return isTimeWithinTimeRanges(item.startTime.toLocalTime(), timeRanges)
}

function addMetadata<T extends RealTimeSttTranscriptElement>(
  item: T,
  isSealed: boolean,
  segmentId: string,
  isInSession: boolean,
  isHearing: boolean,
  isAutoGenerated: boolean,
  isHidden: boolean,
  hearingSectionId: string | undefined,
  sealingMarkerId: string | undefined,
  sessionMarkerId: string | undefined,
  remarkMetadata: RemarkMetadata,
): T & RealTimeSttMetadata {
  return {
    ...item,
    segmentId,
    isInSession,
    isSealed,
    isHearing,
    hearingSectionId,
    sealingMarkerId,
    sessionMarkerId,
    isWithinAutoGeneratedHearing: isAutoGenerated,
    isWithinHiddenSection: isHidden,
    ...remarkMetadata,
  }
}

function getRemarkMetadata(
  item: RealTimeSttTranscriptElement,
  searchHighlights: RealTimeSearchHighlights,
  previousItem: RealTimeSttTranscriptElementWithMetadata | undefined,
): RemarkMetadata {
  if (item.type !== 'Remark') {
    return {
      firstRemarkIdInGroup: '',
    }
  }

  const isFirstRemarkInGroup =
    previousItem?.type !== 'Remark' ||
    previousItem.speakerId !== item.speakerId ||
    previousItem.dialect !== item.dialect
  const firstRemarkIdInGroup = isFirstRemarkInGroup ? item.id : previousItem?.firstRemarkIdInGroup

  return {
    firstRemarkIdInGroup,
    searchHighlightingContext: RealTimeSttRemarkUtils.getSearchHighlightingContextForRemark(item, searchHighlights),
  }
}
